// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TextArea, { TextAreaProps } from "@steiner/MobiusOverrides/TextArea/TextArea";
import Minus from "@steiner/XDL/Icons/Minus";
import Plus from "@steiner/XDL/Icons/Plus";
import { maxLengthNotes } from "@steiner/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    // xng-change
    overrideNotes?: string;
    showAddToProjectsLink?: boolean;
    addToProjectsText?: string;
    addToProjectsClickHandler?: () => void;
    placeholder?: string;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: TypographyPresentationProps;
    // xng-change
    linksWrapper?: InjectableCss;
    showHideLink?: LinkPresentationProps;
    addToProjectsLink?: LinkPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    editableNotesTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
        `,
    },
    label: {
        css: css`
            button {
                color: #2e64b0;
                background: none;
                border: none;
                padding: 0;
                font-size: 15px;
                cursor: pointer;
            }
        `,
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
    // xng-change
    overrideNotes,
    showAddToProjectsLink,
    addToProjectsText,
    addToProjectsClickHandler = () => {},
    placeholder,
}) => {
    const [showLineNotes, setShowLineNotes] = useState(false);
    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNotes);
    };

    // xng-change
    const [notes, setNotes] = useState(overrideNotes ?? cartLine?.notes);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        onNotesChange(notes);
    };

    const heading = showLineNotes ? "Hide Line Notes" : notes.length === 0 ? "Add Line Notes" : "Show Line Notes";

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    // xng-change: change the structure
    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.linksWrapper}>
                    <Link
                        onClick={lineNotesClickHandler}
                        {...styles.showHideLink}
                        {...{ icon: { iconProps: { src: showLineNotes ? Minus : Plus, size: 12 } } }}
                    >
                        {translate(heading)}
                    </Link>
                    {showAddToProjectsLink && (
                        <Link onClick={addToProjectsClickHandler} {...styles.addToProjectsLink}>
                            {addToProjectsText || translate("Product Group")}
                        </Link>
                    )}
                </StyledWrapper>
                {/* XNG-Changes: comment-out OOTB code */}
                {/* <Typography
                    {...styles.label}
                    htmlFor={`notes-${cartLine.id}`}
                    forwardAs="label"
                    onClick={lineNotesClickHandler}
                    data-test-selector="cartline_notesShowHideButton"
                >
                    <button>{translate(heading)}</button>
                </Typography> */}
                {showLineNotes && (
                    <TextArea
                        {...styles.editableNotesTextArea}
                        value={notes}
                        id={`notes-${cartLine.id}`}
                        disabled={!cart.canModifyOrder}
                        onChange={changeHandler}
                        onBlur={blurHandler}
                        data-test-selector="cartline_notes"
                        placeholder={placeholder}
                        type="notes"
                        maxLength={maxLengthNotes}
                    />
                )}
            </StyledWrapper>
        );
    }

    // xng-change
    // if (!cartLine?.notes || !overrideNotes) {
    //     return null;
    // }

    // xng-change
    return (
        // <SmallHeadingAndText
        //     {...styles.readOnlyNotesText}
        //     heading={label || translate("Item Notes")}
        //     text={cartLine.notes}
        // />
        <>
            <Typography {...styles.readOnlyNotesText}>{cartLine?.notes}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.linksWrapper}>
                    {showAddToProjectsLink && (
                        <Link onClick={addToProjectsClickHandler} {...styles.addToProjectsLink}>
                            {addToProjectsText || translate("Product Group")}
                        </Link>
                    )}
                </StyledWrapper>
                {notes && (
                    <TextArea
                        {...styles.editableNotesTextArea}
                        value={notes}
                        id={`notes-${cartLine?.id}`}
                        disabled={true}
                        data-test-selector="cartline_notes"
                        type="notes"
                        maxLength={maxLengthNotes}
                    />
                )}
            </StyledWrapper>
        </>
    );
};

export default withCartLine(CartLineNotes);
